import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Select, { type StylesConfig, type OptionProps, components } from 'react-select';
import { buildReportSelectStyles } from '@/components/Reports/reportSelectStyles';
import * as XLSX from 'xlsx';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReportService from '@services/ReportService';
import { ClearIndicatorComponent as ClearIndicator } from '@/components/Reports/ClearIndicatorComponent';
import './EfficiencyReport.css';
import { useInfoWindow } from '@/contexts/InfoWindowContext';
import { useConversationBus } from '@hooks/useConversationBus';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SelectOption {
  value: number | string;
  label: string;
}

interface EfficRow {
  personId: number;
  employee: string;
  team: string;
  workingcapacity: number;
  ptodays: number;
  loggedhours: string;
  estimated: string;
  externalestimated: string;
  productivehours: string;
  productivity: string;
  efficiency: string;
  burnt: string;
  survey: string;
  sp: string;
  customFields: Record<string, string>;
  status: Record<string, string>;
  timeouts: Record<string, string>;
}

interface TaskItem {
  PersonId: number;
  TaskName: string;
  Estimated: number;
  Remaining: number;
  LoggedHours: number;
  ProductiveHrs: number;
  RemainingInitial: number;
  RemainingFinal: number;
  Link: string;
  conversationID?: string;
}

interface SavedFilter {
  value: string;
  label: string;
  Teams: { Items: (number | string)[] };
  Employees: { Items: (number | string)[] };
  Projects: { Items: (number | string)[] };
  Statuses: { Items: (number | string)[] };
  CardType: { Items: (number | string)[] };
}

type SortDir = 'asc' | 'desc';

// ─── Constants ────────────────────────────────────────────────────────────────

const SAVED_FILTERS_KEY = 'C2_EfficiencyFilters';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getTimeZoneOffset(): string
{
  const offsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = offsetMinutes / 60;
  const sign = offsetHours < 0 ? '+' : '-';
  return `${sign}${Math.abs(Math.floor(offsetHours))}`;
}

function cfHeader(key: string): string
{
  return key.replace('cf_', '').replace(/_/g, ' ').trim();
}

function statusHeader(key: string): string
{
  const s = key.replace(/s_/g, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function timeoutHeader(key: string): string
{
  const toTitle = (s: string) =>
    s
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  const cleaned = key.replace(/_/g, ' ').toLowerCase().trim();
  const parts = cleaned.split('timeout');
  const suffix = parts.slice(1).join('').trim();
  return `Time Out${suffix ? ' ' + toTitle(suffix) : ''}`;
}

function cleanHtml(value: string | null | undefined): string
{
  if (!value) return '';
  const div = document.createElement('div');
  div.innerHTML = value;
  return div.innerText || '';
}

function extractCustomFields(row: Record<string, unknown>): Record<string, string>
{
  const result: Record<string, string> = {};
  for (const [k, v] of Object.entries(row))
  {
    if (k.startsWith('cf_'))
    {
      result[k] = cleanHtml(v as string);
    }
  }
  return result;
}

function extractStatus(row: Record<string, unknown>): Record<string, string>
{
  const result: Record<string, string> = {};
  for (const [k, v] of Object.entries(row))
  {
    if (k.includes('s_'))
    {
      result[k] = parseFloat(String(v ?? 0)).toFixed(0);
    }
  }
  return result;
}

function extractTimeouts(row: Record<string, unknown>): Record<string, string>
{
  const result: Record<string, string> = {};
  for (const [k, v] of Object.entries(row))
  {
    if (k.includes('timeout') || k.includes('block'))
    {
      result[k] = parseFloat(String(v ?? 0)).toFixed(2);
    }
  }
  return result;
}

function mergeDynHeaders(rows: Record<string, string>[]): Record<string, number>
{
  return rows.reduce<Record<string, number>>((acc, r) =>
  {
    for (const k of Object.keys(r))
    {
      acc[k] = (acc[k] ?? 0) + parseFloat(r[k] ?? '0');
    }
    return acc;
  }, {});
}

function loadSavedFilters(): SavedFilter[]
{
  try
  {
    return JSON.parse(localStorage.getItem(SAVED_FILTERS_KEY) || '[]');
  }
  catch
  {
    return [];
  }
}

function persistSavedFilters(filters: SavedFilter[]): void
{
  localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(filters));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface DateInputProps {
  value?: string;
  onClick?: () => void;
  onClear: () => void;
}
const ErDateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ value, onClick, onClear }, ref) => (
    <div className="er-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        readOnly
        className="er-date-input"
        placeholder="MM/DD/YYYY"
        onChange={() => {}}
      />
      <button
        type="button"
        className="er-date-icon-btn"
        onClick={value ? onClear : onClick}
        tabIndex={-1}
      >
        <i className="fa fa-calendar" style={{ fontSize: 12 }} />
      </button>
    </div>
  ),
);
ErDateInput.displayName = 'ErDateInput';

const SortIcon: React.FC<{ col: string; sorted: string; dir: SortDir }> = ({
  col,
  sorted,
  dir,
}) => (
  <i
    className={
      sorted === col ? (dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc') : 'fa fa-sort'
    }
    style={{ display: 'inline-block', width: 20 }}
  />
);

// ─── Main component ───────────────────────────────────────────────────────────

const EfficiencyReport: React.FC = () =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = buildReportSelectStyles(isDark);
  const singleSelectStyles: StylesConfig<SelectOption, false> = {
    ...(selectStyles as StylesConfig<SelectOption, false>),
  };

  // Dropdown options
  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [cardTypeOptions, setCardTypeOptions] = useState<SelectOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<SelectOption[]>([]);

  // Selected filters
  const [selectedTeams, setSelectedTeams] = useState<SelectOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<SelectOption[]>([]);
  const [selectedCardTypes, setSelectedCardTypes] = useState<SelectOption[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<SelectOption[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeInactiveUsers, setIncludeInactiveUsers] = useState(false);

  // Validation
  const [fromError, setFromError] = useState('');
  const [toError, setToError] = useState('');

  // Loading
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Table data
  const [tableData, setTableData] = useState<EfficRow[]>([]);
  const [originalTableData, setOriginalTableData] = useState<EfficRow[]>([]);
  const [cfHeaders, setCfHeaders] = useState<Record<string, number>>({});
  const [statusHeaders, setStatusHeaders] = useState<Record<string, number>>({});
  const [timeoutHeaders, setTimeoutHeaders] = useState<Record<string, number>>({});

  // Summary
  const [summary, setSummary] = useState({
    projects: '',
    leads: '',
    resources: '',
    dateRange: '',
    capacityHours: '',
    workingCapacity: '',
    efficiencyPct: '',
    productivityPct: '',
    productivityHrs: '',
    burned: '',
  });

  // Sort
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Tasks
  const [allTasks, setAllTasks] = useState<TaskItem[]>([]);
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [modalTasks, setModalTasks] = useState<TaskItem[]>([]);
  const [originalModalTasks, setOriginalModalTasks] = useState<TaskItem[]>([]);
  const [personSelected, setPersonSelected] = useState('');
  const [modalSortCol, setModalSortCol] = useState('');
  const [modalSortDir, setModalSortDir] = useState<SortDir>('asc');

  // Saved filters
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState<SelectOption | null>(null);
  const [showFilterNameModal, setShowFilterNameModal] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterNameWarning, setFilterNameWarning] = useState('');
  const [filterToEdit, setFilterToEdit] = useState<string | null>(null);

  // Date range notification
  const [showDateRangeMsg, setShowDateRangeMsg] = useState(false);
  const dateRangeMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { openInfoWindow } = useInfoWindow();

  useConversationBus('conversation-updated', null, () =>
  {
    if (tableData.length > 0) applyFilters();
  });

  // ── Initial data loads ──────────────────────────────────────────────────────

  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    // Teams
    ReportService.GetAllTeams()
      .then((data: any[]) =>
      {
        setTeamOptions((data || []).map(t => ({ value: t.BasicFieldValueId, label: t.Value })));
      })
      .catch(() => {});

    // Card types
    ReportService.GetAllCard()
      .then((data: any[]) =>
      {
        setCardTypeOptions((data || []).map(c => ({ value: c.SideMenuId, label: c.Text })));
      })
      .catch(() => {});

    // Projects
    ReportService.GetAllProject({ GroupByDetailId: 0, GroupByLayerId: 0, GroupByValueField: '' })
      .then((data: any) =>
      {
        setProjectOptions(
          ((data?.Projects as any[]) || []).map(p => ({ value: p.ProjectID, label: p.Name })),
        );
      })
      .catch(() => {});

    // Statuses
    ReportService.GetStatusList({})
      .then((data: any[]) =>
      {
        setStatusOptions((data || []).map(s => ({ value: s.Id, label: s.Value })));
      })
      .catch(() => {});

    // Saved filters from localStorage
    setSavedFilters(loadSavedFilters());
  }, []);

  // Reload employees when teams or includeInactiveUsers change
  useEffect(() =>
  {
    const teams = selectedTeams.map(t => t.value).join(',');
    ReportService.GetEfficiencyAsignedTo({
      teams,
      artistCategory: '',
      department: '',
      getAll: includeInactiveUsers,
    })
      .then((data: any[]) =>
      {
        setEmployeeOptions((data || []).map(e => ({ value: e.PersonID, label: e.Name })));
      })
      .catch(() => {});
  }, [selectedTeams, includeInactiveUsers]);

  // ── Validation ──────────────────────────────────────────────────────────────

  function validate(): boolean
  {
    let ok = true;
    if (!fromDate)
    {
      setFromError('You have not selected a Date.');
      ok = false;
    }
    else
    {
      setFromError('');
    }
    if (!toDate)
    {
      setToError('You have not selected a Date.');
      ok = false;
    }
    else
    {
      setToError('');
    }
    if (fromDate && toDate)
    {
      if (moment(toDate).isBefore(moment(fromDate)))
      {
        setFromError('The from date must be less than the to date.');
        ok = false;
      }
      else if (moment(fromDate).isBefore(moment().subtract(2, 'years')))
      {
        if (dateRangeMsgTimerRef.current) clearTimeout(dateRangeMsgTimerRef.current);
        setShowDateRangeMsg(true);
        dateRangeMsgTimerRef.current = setTimeout(() => setShowDateRangeMsg(false), 3000);
        return false;
      }
    }
    return ok;
  }

  // ── Apply filters ───────────────────────────────────────────────────────────

  const applyFilters = useCallback(async () =>
  {
    if (!validate()) return;

    setLoading(true);
    setHasSearched(true);
    setTableData([]);
    setOriginalTableData([]);
    setSortCol('');
    setSummary({
      projects: '',
      leads: '',
      resources: '',
      dateRange: '',
      capacityHours: '',
      workingCapacity: '',
      efficiencyPct: '',
      productivityPct: '',
      productivityHrs: '',
      burned: '',
    });

    const request = {
      Teams: selectedTeams.map(t => t.value).join(','),
      AsignedTo: selectedEmployees.map(e => e.value).join(','),
      DateTime: moment(fromDate).format('YYYYMMDD'),
      Duedate: moment(toDate).format('YYYYMMDD'),
      projectId: selectedProjects.map(p => p.value).join(','),
      sideMenuId: selectedCardTypes.map(c => c.value).join(','),
      layersId: '',
      dashboardsId: '',
      showGroup: true,
      showAttendanceGroup: false,
      getAll: includeInactiveUsers,
      timeZone: getTimeZoneOffset(),
      comments: '',
      employeeids: '',
      status: selectedStatuses.map(s => s.value).join(','),
      artistCategory: '',
      departments: '',
    };

    try
    {
      const resp: any = await ReportService.GetEfficiencyReport(request);
      const tableRows: any[] = resp?.Table || [];
      const tasks: TaskItem[] = (resp?.Tasks || []).map((t: any) => ({
        ...t,
        conversationID: t.Link ? t.Link.split('/').pop() : '',
      }));
      setAllTasks(tasks);

      if (!tableRows.length)
      {
        setLoading(false);
        return;
      }

      // Process rows
      const processed: EfficRow[] = tableRows.map((r: any) => ({
        personId: r.PersonId,
        employee: r.Employees,
        team: r.Teams,
        workingcapacity: r.WorkingCapacity,
        ptodays: r.PTODays,
        loggedhours: parseFloat(r.LoggedHours ?? 0).toFixed(2),
        estimated: parseFloat(r.Estimated ?? 0).toFixed(2),
        externalestimated: parseFloat(r.ExternalEstimated ?? 0).toFixed(2),
        productivehours: parseFloat(r.ProductiveHours ?? 0).toFixed(2),
        productivity: parseFloat(r.Productivity ?? 0).toFixed(2),
        efficiency: parseFloat(r.Efficiency ?? 0).toFixed(2),
        burnt: parseFloat(r.BurnedHours ?? 0).toFixed(2),
        survey: parseFloat(r.Survey ?? 0).toFixed(2),
        sp: parseFloat(r.SP ?? 0).toFixed(0),
        customFields: extractCustomFields(r),
        status: extractStatus(r),
        timeouts: extractTimeouts(r),
      }));

      // Dynamic column headers
      setCfHeaders(mergeDynHeaders(processed.map(r => r.customFields)));
      setStatusHeaders(mergeDynHeaders(processed.map(r => r.status)));
      setTimeoutHeaders(mergeDynHeaders(processed.map(r => r.timeouts)));

      setTableData(processed);
      setOriginalTableData(processed);

      // Summary
      const dateRangeStr = `${moment(fromDate).format('MM/DD/YYYY')} - ${moment(toDate).format('MM/DD/YYYY')}`;
      const uniqueProjects = Array.from(
        new Set(
          tableRows.flatMap((r: any) =>
            !r.Projects ? [] : r.Projects.split(',').map((s: string) => s.trim()),
          ),
        ),
      ).join(', ');
      const resources = Array.from(new Set(tableRows.map((r: any) => r.Employees))).join(', ');
      const capacityHours =
        (
          tableRows.reduce((s: number, r: any) => s + (r.WorkingCapacity ?? 0), 0) +
          tableRows.reduce((s: number, r: any) => s + (r.PTODays ?? 0), 0) * 8
        ).toFixed(2) + ' hours';
      const workingCapacity =
        tableRows.reduce((s: number, r: any) => s + (r.WorkingCapacity ?? 0), 0).toFixed(2) +
        ' hours';
      void tableRows.reduce((s: number, r: any) => s + parseFloat(r.LoggedHours ?? 0), 0);
      const totalProductiveHours = tableRows.reduce(
        (s: number, r: any) => s + parseFloat(r.ProductiveHours ?? 0),
        0,
      );
      const totalWorkingCapacity = tableRows.reduce(
        (s: number, r: any) => s + (r.WorkingCapacity ?? 0),
        0,
      );
      const efficiencyArr = tableRows.map((r: any) => parseFloat(r.Efficiency ?? 0));
      const efficiencyPct =
        (
          efficiencyArr.reduce((s: number, v: number) => s + v, 0) / (efficiencyArr.length || 1)
        ).toFixed(2) + '%';
      const productivityPct =
        totalWorkingCapacity > 0
          ? ((totalProductiveHours * 100) / totalWorkingCapacity).toFixed(2) + '%'
          : '0.00%';
      const productivityHrs = totalProductiveHours.toFixed(2) + ' hours';
      const burned =
        tableRows.reduce((s: number, r: any) => s + parseFloat(r.BurnedHours ?? 0), 0).toFixed(2) +
        ' hours';

      setSummary({
        projects: uniqueProjects,
        leads: 'N/A',
        resources,
        dateRange: dateRangeStr,
        capacityHours,
        workingCapacity,
        efficiencyPct,
        productivityPct,
        productivityHrs,
        burned,
      });
    }
    catch (e)
    {
      console.error('EfficiencyReport error:', e);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTeams,
    selectedEmployees,
    selectedCardTypes,
    selectedProjects,
    selectedStatuses,
    fromDate,
    toDate,
    includeInactiveUsers,
  ]);

  // ── Sort ────────────────────────────────────────────────────────────────────

  const handleSort = (col: string) =>
  {
    const newDir = sortCol === col && sortDir === 'asc' ? 'desc' : 'asc';
    setSortCol(col);
    setSortDir(newDir);

    const sorted = [...originalTableData].sort((a, b) =>
    {
      let av: any;
      let bv: any;
      if (col.startsWith('cf_'))
      {
        av = a.customFields[col] ?? '';
        bv = b.customFields[col] ?? '';
      }
      else if (col.includes('s_'))
      {
        av = parseFloat(a.status[col] ?? '0');
        bv = parseFloat(b.status[col] ?? '0');
      }
      else if (col.includes('timeout') || col.includes('block'))
      {
        av = parseFloat(a.timeouts[col] ?? '0');
        bv = parseFloat(b.timeouts[col] ?? '0');
      }
      else
      {
        av = (a as any)[col] ?? '';
        bv = (b as any)[col] ?? '';
      }
      if (typeof av === 'string' && typeof bv === 'string')
      {
        return newDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return newDir === 'asc' ? av - bv : bv - av;
    });
    setTableData(sorted);
  };

  // ── Tasks modal ─────────────────────────────────────────────────────────────

  const openTasksModal = (row: EfficRow) =>
  {
    const tasks = allTasks.filter(t => t.PersonId === row.personId);
    setModalTasks(tasks);
    setOriginalModalTasks(tasks);
    setPersonSelected(row.employee);
    setModalSortCol('');
    setShowTasksModal(true);
  };

  const handleModalSort = (col: string) =>
  {
    const newDir = modalSortCol === col && modalSortDir === 'asc' ? 'desc' : 'asc';
    setModalSortCol(col);
    setModalSortDir(newDir);
    const sorted = [...originalModalTasks].sort((a, b) =>
    {
      const av = (a as any)[col] ?? '';
      const bv = (b as any)[col] ?? '';
      if (typeof av === 'string' && typeof bv === 'string')
        return newDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return newDir === 'asc' ? av - bv : bv - av;
    });
    setModalTasks(sorted);
  };

  // ── Saved filters ───────────────────────────────────────────────────────────

  const canSave =
    selectedTeams.length > 0 ||
    selectedEmployees.length > 0 ||
    selectedProjects.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedCardTypes.length > 0;

  const openSaveFilterModal = () =>
  {
    setFilterName('');
    setFilterNameWarning('');
    setFilterToEdit(null);
    setShowFilterNameModal(true);
  };

  const openEditFilterModal = (label: string) =>
  {
    setFilterName(label);
    setFilterNameWarning('');
    setFilterToEdit(label);
    setShowFilterNameModal(true);
  };

  const closeFilterModal = () =>
  {
    setShowFilterNameModal(false);
    setFilterName('');
    setFilterNameWarning('');
    setFilterToEdit(null);
  };

  const confirmSaveFilter = () =>
  {
    const name = filterName.trim();
    if (!name) return;
    const current = loadSavedFilters();

    if (filterToEdit !== null)
    {
      // Edit existing
      const updated = current.map(f => (f.label === filterToEdit ? { ...f, label: name } : f));
      // Check duplicate (excluding self)
      if (updated.some((f, i) => f.label === name && current[i]?.label !== filterToEdit))
      {
        setFilterNameWarning('Name not available');
        return;
      }
      persistSavedFilters(updated);
      setSavedFilters(updated);
    }
    else
    {
      // New filter
      if (current.some(f => f.label === name))
      {
        setFilterNameWarning('Name not available');
        return;
      }
      const newFilter: SavedFilter = {
        value: new Date().toISOString(),
        label: name,
        Teams: { Items: selectedTeams.map(t => t.value) },
        Employees: { Items: selectedEmployees.map(e => e.value) },
        Projects: { Items: selectedProjects.map(p => p.value) },
        Statuses: { Items: selectedStatuses.map(s => s.value) },
        CardType: { Items: selectedCardTypes.map(c => c.value) },
      };
      const updated = [...current, newFilter];
      persistSavedFilters(updated);
      setSavedFilters(updated);
    }
    closeFilterModal();
  };

  const deleteFilter = (value: string) =>
  {
    const updated = loadSavedFilters().filter(f => f.value !== value);
    persistSavedFilters(updated);
    setSavedFilters(updated);
    if ((selectedSavedFilter as any)?.value === value)
    {
      setSelectedSavedFilter(null);
    }
  };

  const applyLoadedFilter = async (option: SelectOption | null) =>
  {
    setSelectedSavedFilter(option);
    if (!option) return;
    const filter = savedFilters.find(f => f.value === option.value);
    if (!filter) return;
    setSelectedTeams(teamOptions.filter(t => filter.Teams.Items.includes(t.value)));
    setSelectedProjects(projectOptions.filter(p => filter.Projects.Items.includes(p.value)));
    setSelectedStatuses(statusOptions.filter(s => filter.Statuses.Items.includes(s.value)));
    setSelectedCardTypes(cardTypeOptions.filter(c => filter.CardType.Items.includes(c.value)));
    const employees: any[] = await ReportService.GetEfficiencyAsignedTo({
      teams: teamOptions
        .filter(t => filter.Teams.Items.includes(t.value))
        .map(t => t.value)
        .join(','),
      artistCategory: '',
      department: '',
      getAll: includeInactiveUsers,
    });
    setSelectedEmployees(
      employees
        .filter(e => filter.Employees.Items.includes(e.PersonID))
        .map(e => ({ value: e.PersonID, label: e.Name })) ?? [],
    );
  };

  const savedFilterOptions: SelectOption[] = savedFilters.map(f => ({
    value: f.value,
    label: f.label,
  }));

  // ── Export ──────────────────────────────────────────────────────────────────

  const exportToExcel = () =>
  {
    if (!tableData.length) return;

    const cfKeys = Object.keys(cfHeaders);
    const statusKeys = Object.keys(statusHeaders);
    const timeoutKeys = Object.keys(timeoutHeaders);

    const headers = [
      'Employee',
      'Team',
      ...cfKeys.map(cfHeader),
      'Working Capacity',
      'PTO Days',
      'Logged Hours',
      'Productive Hours',
      'External Estimate',
      'Estimate',
      'Productivity',
      'Efficiency',
      'Burned Hours',
      'Survey',
      'SP',
      ...statusKeys.map(statusHeader),
      ...timeoutKeys.map(timeoutHeader),
    ];

    const rows = tableData.map(r => ({
      employee: r.employee,
      team: r.team,
      ...Object.fromEntries(cfKeys.map(k => [cfHeader(k), r.customFields[k] ?? ''])),
      workingcapacity: r.workingcapacity,
      ptodays: r.ptodays,
      loggedhours: parseFloat(r.loggedhours),
      productivehours: parseFloat(r.productivehours),
      externalestimated: parseFloat(r.externalestimated),
      estimated: parseFloat(r.estimated),
      productivity: parseFloat(r.productivity),
      efficiency: parseFloat(r.efficiency),
      burnt: parseFloat(r.burnt),
      survey: parseFloat(r.survey),
      sp: parseFloat(r.sp),
      ...Object.fromEntries(statusKeys.map(k => [statusHeader(k), r.status[k] ?? ''])),
      ...Object.fromEntries(timeoutKeys.map(k => [timeoutHeader(k), r.timeouts[k] ?? ''])),
    }));

    // Combined sheet: summary + data
    const sheet = XLSX.utils.aoa_to_sheet([]);
    const infoRows = [
      {
        key: 'Project(s)',
        value: summary.projects,
        key2: 'Productivity',
        value2: summary.productivityPct,
        formula: 'Productivity = (ProductiveHours * 100) / WorkingCapacity',
      },
      {
        key: 'Lead(s)',
        value: summary.leads,
        key2: 'Efficiency %',
        value2: summary.efficiencyPct,
        formula: 'Efficiency = (ProductiveHours * 100) / LoggedHours',
      },
      {
        key: 'Resources',
        value: summary.resources,
        key2: 'Burned',
        value2: summary.burned,
        formula: 'BurnedHours = LoggedHours - ProductiveHours',
      },
      {
        key: 'Date Range',
        value: summary.dateRange,
        key2: 'Productivity Hrs',
        value2: summary.productivityHrs,
        formula: 'ProductiveHrs = RemainingInitial - RemainingFinal',
      },
      {
        key: 'Capacity Hours',
        value: summary.capacityHours,
        key2: 'Working Capacity',
        value2: summary.workingCapacity,
        formula: '',
      },
    ];
    infoRows.forEach((row, i) =>
    {
      sheet[`A${i + 1}`] = { v: row.key, t: 's' };
      sheet[`B${i + 1}`] = { v: row.value, t: 's' };
      sheet[`C${i + 1}`] = { v: row.key2, t: 's' };
      sheet[`D${i + 1}`] = { v: row.value2, t: 's' };
      sheet[`E${i + 1}`] = { v: row.formula, t: 's' };
    });
    const dataStartRow = infoRows.length + 4;
    const dataSheet = XLSX.utils.json_to_sheet(rows);
    const range = XLSX.utils.decode_range(dataSheet['!ref'] || 'A1');
    for (let R = range.s.r; R <= range.e.r; R++)
    {
      for (let C = range.s.c; C <= range.e.c; C++)
      {
        const srcCell = XLSX.utils.encode_cell({ r: R, c: C });
        const dstCell = XLSX.utils.encode_cell({ r: R + dataStartRow, c: C });
        if (dataSheet[srcCell])
        {
          sheet[dstCell] = R === 0 ? { v: headers[C] ?? '', t: 's' } : dataSheet[srcCell];
        }
      }
    }
    const lastRow = dataStartRow + range.e.r + 1;
    const lastCol = Math.max(4, range.e.c);
    sheet['!ref'] = XLSX.utils.encode_range({ r: 0, c: 0 }, { r: lastRow, c: lastCol });
    sheet['!cols'] = Array(lastCol + 1).fill({ wch: 25 });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Efficiency Report');
    XLSX.writeFile(wb, 'Efficiency.xlsx');
  };

  // ── Key handler for modal (close on Escape) ─────────────────────────────────

  useEffect(() =>
  {
    const handler = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape')
      {
        closeFilterModal();
        setShowTasksModal(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <div id="EfficiencyReport">
        <h3 className="er-title">Efficiency Report</h3>
        <p className="er-subtitle">
          This report contains information about the user's efficiency.
          <br />
          Dashboard filter will only display information from Default Columns or Dynamic Column,
          never from both.
        </p>

        {/* Saved Filters row */}
        <div className="er-saved-row">
          <div className="er-saved-item">
            <label className="er-label">Saved Filters:</label>
            <Select
              options={savedFilterOptions}
              value={selectedSavedFilter}
              onChange={v => applyLoadedFilter(v as SelectOption | null)}
              isClearable
              placeholder="Select..."
              styles={singleSelectStyles}
              className="er-select"
              components={{
                Option: (props: OptionProps<SelectOption, false>) =>
                {
                  const opt = props.data as SelectOption;
                  return (
                    <components.Option {...props}>
                      <div className="er-filter-option">
                        <span className="er-filter-option-label">{opt.label}</span>
                        <span className="er-filter-option-actions">
                          <button
                            className="er-filter-icon-btn"
                            title="Edit name"
                            onMouseDown={e =>
                            {
                              e.preventDefault();
                              e.stopPropagation();
                              openEditFilterModal(opt.label);
                            }}
                          >
                            <i className="fa fa-pencil" />
                          </button>
                          <button
                            className="er-filter-icon-btn"
                            title="Delete"
                            onMouseDown={e =>
                            {
                              e.preventDefault();
                              e.stopPropagation();
                              deleteFilter(String(opt.value));
                            }}
                          >
                            <i className="fa fa-times" />
                          </button>
                        </span>
                      </div>
                    </components.Option>
                  );
                },
              }}
            />
          </div>
        </div>

        <div className="er-filters">
          {/* Row 1: team, employee, card type, project, status, save btn */}
          <div className="er-filter-row">
            <div className="er-filter-item">
              <label className="er-label">Team(s):</label>
              <Select
                isMulti
                options={teamOptions}
                value={selectedTeams}
                onChange={v => setSelectedTeams(v as SelectOption[])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                className="er-select"
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="er-filter-item">
              <label className="er-label">Employee(s):</label>
              <Select
                isMulti
                options={employeeOptions}
                value={selectedEmployees}
                onChange={v => setSelectedEmployees(v as SelectOption[])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                className="er-select"
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="er-filter-item">
              <label className="er-label">Card Type(s):</label>
              <Select
                isMulti
                options={cardTypeOptions}
                value={selectedCardTypes}
                onChange={v => setSelectedCardTypes(v as SelectOption[])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                className="er-select"
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="er-filter-item">
              <label className="er-label">Project(s):</label>
              <Select
                isMulti
                options={projectOptions}
                value={selectedProjects}
                onChange={v => setSelectedProjects(v as SelectOption[])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                className="er-select"
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="er-filter-item">
              <label className="er-label">Status:</label>
              <Select
                isMulti
                options={statusOptions}
                value={selectedStatuses}
                onChange={v => setSelectedStatuses(v as SelectOption[])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                className="er-select"
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            {/* from, to, include inactive, then save filters continue in the same grid */}
            <div className="er-filter-item er-filter-item--date">
              <label className="er-label">From:</label>
              <DatePicker
                selected={fromDate ? moment(fromDate, 'YYYY-MM-DD').toDate() : null}
                onChange={(d: any) => setFromDate(d ? moment(d).format('YYYY-MM-DD') : '')}
                maxDate={new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)}
                dateFormat="MM/dd/yyyy"
                customInput={<ErDateInput onClear={() => setFromDate('')} />}
                popperProps={{ strategy: 'fixed' }}
                showPopperArrow={false}
                calendarClassName="er-datepicker-calendar"
              />
              {fromError && <span className="er-validation-msg">{fromError}</span>}
            </div>
            <div className="er-filter-item er-filter-item--date">
              <label className="er-label">To:</label>
              <DatePicker
                selected={toDate ? moment(toDate, 'YYYY-MM-DD').toDate() : null}
                onChange={(d: any) => setToDate(d ? moment(d).format('YYYY-MM-DD') : '')}
                maxDate={new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)}
                dateFormat="MM/dd/yyyy"
                customInput={<ErDateInput onClear={() => setToDate('')} />}
                popperProps={{ strategy: 'fixed' }}
                showPopperArrow={false}
                calendarClassName="er-datepicker-calendar"
              />
              {toError && <span className="er-validation-msg">{toError}</span>}
            </div>
            <div className="er-filter-item--btn">
              <button
                className="er-save-filters-btn"
                disabled={!canSave}
                onClick={openSaveFilterModal}
              >
                Save Filters
              </button>
            </div>
          </div>

          <div className="er-filter-actions">
            <button className="er-apply-btn" onClick={applyFilters} disabled={loading}>
              {loading ? 'Loading...' : 'Apply Filters'}
            </button>
            <label className="er-checkbox-label">
              <input
                type="checkbox"
                checked={includeInactiveUsers}
                onChange={e => setIncludeInactiveUsers(e.target.checked)}
              />
              <span>Include Inactive Users</span>
            </label>
          </div>
        </div>

        {/* Table wrapper */}
        <div className="er-table-wrapper">
          <div className="er-export-row">
            <button
              className="er-export-btn"
              onClick={exportToExcel}
              disabled={!tableData.length}
              title="Export to Excel"
            >
              <span className="material-icons style-icon-cloud">cloud_download</span> Export
            </button>
          </div>

          {/* Summary table - always visible */}
          <div className="er-summary-wrapper">
            <table className="er-summary-table">
              <tbody>
                <tr>
                  <td>Project(s)</td>
                  <td>{summary.projects}</td>
                  <td>Productivity</td>
                  <td>{summary.productivityPct}</td>
                  <td>Formula</td>
                  <td className="er-summary-formula">
                    Productivity = (ProductiveHours * 100) / WorkingCapacity
                  </td>
                </tr>
                <tr>
                  <td>Lead(s)</td>
                  <td>{summary.leads}</td>
                  <td>Efficiency %</td>
                  <td>{summary.efficiencyPct}</td>
                  <td>Formula</td>
                  <td className="er-summary-formula">
                    Efficiency = (ProductiveHours * 100) / LoggedHours
                  </td>
                </tr>
                <tr>
                  <td>Resources</td>
                  <td>{summary.resources}</td>
                  <td>Burned</td>
                  <td>{summary.burned}</td>
                  <td>Formula</td>
                  <td className="er-summary-formula">
                    BurnedHours = LoggedHours - ProductiveHours
                  </td>
                </tr>
                <tr>
                  <td>Date Range</td>
                  <td>{summary.dateRange}</td>
                  <td>Productivity Hrs</td>
                  <td>{summary.productivityHrs}</td>
                  <td>Formula</td>
                  <td className="er-summary-formula">
                    ProductiveHrs = RemainingInitial - RemainingFinal
                  </td>
                </tr>
                <tr>
                  <td>Capacity Hours</td>
                  <td>{summary.capacityHours}</td>
                  <td>Working Capacity</td>
                  <td>{summary.workingCapacity}</td>
                  <td className="er-summary-note" colSpan={2}>
                    Working capacity is calculated using the report's date range. If the end date is
                    later than today's date, the calculation will use today's date instead, based on
                    8-hour workdays.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Data table */}
          <div className="er-table-scroll">
            <table className="er-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('employee')}>
                    Employees <SortIcon col="employee" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('team')}>
                    Team <SortIcon col="team" sorted={sortCol} dir={sortDir} />
                  </th>
                  {Object.keys(cfHeaders).map(k => (
                    <th key={k} onClick={() => handleSort(k)}>
                      {cfHeader(k)} <SortIcon col={k} sorted={sortCol} dir={sortDir} />
                    </th>
                  ))}
                  <th onClick={() => handleSort('workingcapacity')}>
                    Working Capacity{' '}
                    <SortIcon col="workingcapacity" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('ptodays')}>
                    PTO Days <SortIcon col="ptodays" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('loggedhours')}>
                    Logged Hours <SortIcon col="loggedhours" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('productivehours')}>
                    Productive Hours{' '}
                    <SortIcon col="productivehours" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('externalestimated')}>
                    External Estimated{' '}
                    <SortIcon col="externalestimated" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('estimated')}>
                    Estimated <SortIcon col="estimated" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('productivity')}>
                    Productivity <SortIcon col="productivity" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('efficiency')}>
                    Efficiency <SortIcon col="efficiency" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('burnt')}>
                    Burned <SortIcon col="burnt" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('survey')}>
                    Survey <SortIcon col="survey" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('sp')}>
                    SP <SortIcon col="sp" sorted={sortCol} dir={sortDir} />
                  </th>
                  {Object.keys(statusHeaders).map(k => (
                    <th key={k} onClick={() => handleSort(k)}>
                      {statusHeader(k)} <SortIcon col={k} sorted={sortCol} dir={sortDir} />
                    </th>
                  ))}
                  {Object.keys(timeoutHeaders).map(k => (
                    <th key={k} onClick={() => handleSort(k)}>
                      {timeoutHeader(k)} <SortIcon col={k} sorted={sortCol} dir={sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => (
                  <tr key={i}>
                    <td>
                      <button
                        className="er-employee-link"
                        onClick={() => openTasksModal(row)}
                        title={row.employee}
                      >
                        {row.employee}
                      </button>
                    </td>
                    <td>{row.team}</td>
                    {Object.keys(cfHeaders).map(k => (
                      <td key={k}>{row.customFields[k] ?? ''}</td>
                    ))}
                    <td>{row.workingcapacity}</td>
                    <td>{row.ptodays}</td>
                    <td>{row.loggedhours}</td>
                    <td>{row.productivehours}</td>
                    <td>{row.externalestimated}</td>
                    <td>{row.estimated}</td>
                    <td>{row.productivity}</td>
                    <td>{row.efficiency}</td>
                    <td>{row.burnt}</td>
                    <td>{row.survey}</td>
                    <td>{row.sp}</td>
                    {Object.keys(statusHeaders).map(k => (
                      <td key={k}>{row.status[k] ?? ''}</td>
                    ))}
                    {Object.keys(timeoutHeaders).map(k => (
                      <td key={k}>{row.timeouts[k] ?? ''}</td>
                    ))}
                  </tr>
                ))}
                {!loading && tableData.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        14 +
                        Object.keys(cfHeaders).length +
                        Object.keys(statusHeaders).length +
                        Object.keys(timeoutHeaders).length
                      }
                      className="er-no-data"
                    >
                      {hasSearched
                        ? 'No results found'
                        : 'Please select at least a date range to display results.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Filter Name Modal */}
        {showFilterNameModal && (
          <div className="er-modal-overlay" onClick={closeFilterModal}>
            <div className="er-modal" onClick={e => e.stopPropagation()}>
              <div className="er-modal-header">
                <h4 className="er-modal-title">Filter Name</h4>
                <button className="er-modal-close" onClick={closeFilterModal}>
                  <i className="icon-incorrect-icon" />
                </button>
              </div>
              <input
                type="text"
                className="er-filter-name-input"
                maxLength={100}
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
                onKeyDown={e =>
                {
                  if (e.key === 'Enter')
                  {
                    e.preventDefault();
                    confirmSaveFilter();
                  }
                }}
                autoFocus
              />
              {filterNameWarning && (
                <div className="er-filter-name-warning">{filterNameWarning}</div>
              )}
              <div className="er-modal-actions">
                <button className="er-btn-cancel" onClick={closeFilterModal}>
                  Cancel
                </button>
                <button
                  className="er-btn-confirm"
                  disabled={!filterName.trim()}
                  onClick={confirmSaveFilter}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tasks Modal */}
        {showTasksModal && (
          <div className="er-modal-overlay" onClick={() => setShowTasksModal(false)}>
            <div className="er-modal er-modal--tasks" onClick={e => e.stopPropagation()}>
              <div className="er-modal-header">
                <h4 className="er-modal-title">{personSelected}'s tasks</h4>
                <button className="er-modal-close" onClick={() => setShowTasksModal(false)}>
                  <i className="icon-incorrect-icon" />
                </button>
              </div>
              <div className="er-tasks-scroll">
                <table className="er-tasks-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleModalSort('TaskName')}>
                        Task Name{' '}
                        <SortIcon col="TaskName" sorted={modalSortCol} dir={modalSortDir} />
                      </th>
                      <th onClick={() => handleModalSort('Estimated')}>
                        Estimated{' '}
                        <SortIcon col="Estimated" sorted={modalSortCol} dir={modalSortDir} />
                      </th>
                      <th onClick={() => handleModalSort('Remaining')}>
                        Remaining{' '}
                        <SortIcon col="Remaining" sorted={modalSortCol} dir={modalSortDir} />
                      </th>
                      <th onClick={() => handleModalSort('LoggedHours')}>
                        Logged Hours{' '}
                        <SortIcon col="LoggedHours" sorted={modalSortCol} dir={modalSortDir} />
                      </th>
                      <th onClick={() => handleModalSort('ProductiveHrs')}>
                        Productive Hours{' '}
                        <SortIcon col="ProductiveHrs" sorted={modalSortCol} dir={modalSortDir} />
                      </th>
                      <th onClick={() => handleModalSort('RemainingInitial')}>
                        Remaining Initial{' '}
                        <SortIcon col="RemainingInitial" sorted={modalSortCol} dir={modalSortDir} />
                      </th>
                      <th onClick={() => handleModalSort('RemainingFinal')}>
                        Remaining Final{' '}
                        <SortIcon col="RemainingFinal" sorted={modalSortCol} dir={modalSortDir} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalTasks.map((task, i) => (
                      <tr key={i}>
                        <td title={task.TaskName}>
                          <a
                            className="er-employee-link"
                            onClick={() =>
                            {
                              if (task.conversationID)
                              {
                                const conversationId = parseInt(task.conversationID, 10);
                                if (!isNaN(conversationId))
                                {
                                  openInfoWindow(conversationId);
                                }
                              }
                            }}
                          >
                            {task.TaskName}
                          </a>
                        </td>
                        <td>{task.Estimated}</td>
                        <td>{task.Remaining}</td>
                        <td>{task.LoggedHours}</td>
                        <td>{task.ProductiveHrs}</td>
                        <td>{task.RemainingInitial}</td>
                        <td>{task.RemainingFinal}</td>
                      </tr>
                    ))}
                    {modalTasks.length === 0 && (
                      <tr>
                        <td colSpan={7} className="er-no-data">
                          No tasks found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {showDateRangeMsg &&
        createPortal(
          <>
            <style>{`
            @keyframes er-date-msg {
              0%   { opacity: 0; }
              10%  { opacity: 1; }
              90%  { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
            <div
              style={{
                position: 'fixed',
                top: '64px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#62abcb',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                pointerEvents: 'none',
                zIndex: 9999,
                whiteSpace: 'nowrap',
                animation: 'er-date-msg 3s ease forwards',
              }}
            >
              The start date must be within the last 2 years
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default EfficiencyReport;
